import { useEffect, useRef, useState } from "react";
import { auth, db } from "./firebase";

import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  setPersistence,
  browserSessionPersistence
} from "firebase/auth";

import {
  doc,
  getDoc
} from "firebase/firestore";

import Dashboard from "./Dashboard";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import {
  setUserOffline,
  startUserPresence
} from "./services/presenceService";
import { isCustomerPortalHostname } from "./services/supportService";

const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "argevioLastActivityAt";

function App() {
  const [email, setEmail] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("argevioView") === "customer"
      ? params.get("supportEmail") || ""
      : "";
  });
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState("");
  const [loginMessageType, setLoginMessageType] = useState("error");
  const isSupportTestLogin = new URLSearchParams(window.location.search).get("argevioView") === "customer";
  const isCustomerPortal = isCustomerPortalHostname();
  const requestedSupportEmail = new URLSearchParams(window.location.search).get("supportEmail") || "destek@argevio.com";
  const sessionTimeoutRef = useRef(null);

  useEffect(() => {
    let unsubscribe = () => {};
    let isMounted = true;

    const initializeAuth = async () => {
      await setPersistence(auth, browserSessionPersistence);

      if (!isMounted) {
        return;
      }

      unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (
        currentUser &&
        isSupportTestLogin &&
        String(currentUser.email || "").toLowerCase() !== requestedSupportEmail.toLowerCase()
      ) {
        await signOut(auth);
        setUser(null);
        setUserRole(null);
        setUserData(null);
        setAuthLoading(false);
        return;
      }
      setUser(currentUser);

      if (currentUser) {
        try {
          const lastActivityAt = Number(
            sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || 0
          );

          if (lastActivityAt && Date.now() - lastActivityAt >= SESSION_TIMEOUT_MS) {
            await setUserOffline(currentUser.uid);
            await signOut(auth);

            sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
            setUser(null);
            setLoginMessageType("error");
            setLoginError("Güvenliğiniz için 60 dakika işlem yapılmadığı için oturumunuz kapatıldı.");
            setUserRole(null);
            setUserData(null);
            setAuthLoading(false);

            return;
          }

          if (!lastActivityAt) {
            sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
          }

          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);

          // Kullanıcı kaydı yoksa çıkış yap
          if (!userSnap.exists()) {
            console.error("Kullanıcı yetki kaydı bulunamadı.");

            await signOut(auth);

            setUserRole(null);
            setAuthLoading(false);

            return;
          }

          const userData = userSnap.data();

          // Kullanıcı pasif ise çıkış yap
          if (userData.isActive === false) {
            console.error("Kullanıcı pasif.");

            await signOut(auth);

            setUserRole(null);
            setUserData(null);
            setAuthLoading(false);

            return;
          }

          setUserRole(userData.role || "viewer");
          setUserData(userData);
        } catch (error) {
          console.error("Yetki kontrol hatası:", error);

          await signOut(auth);

          setUserRole(null);
        }
      } else {
        setUserRole(null);
        setUserData(null);
      }

      setAuthLoading(false);
      });
    };

    initializeAuth().catch(async (error) => {
      console.error("Oturum başlatma hatası:", error);

      await signOut(auth);

      if (isMounted) {
        setUser(null);
        setUserRole(null);
        setUserData(null);
        setAuthLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [isSupportTestLogin, requestedSupportEmail]);

  useEffect(() => {
    if (!user || !userData) return undefined;

    return startUserPresence({
      uid: user.uid,
      email: user.email,
      name: userData.name,
      role: userData.role || userRole,
      photoURL: userData.photoURL || user.photoURL
    });
  }, [user, userData, userRole]);

  useEffect(() => {
    if (!user) {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }

      return undefined;
    }

    const logoutDueToInactivity = async () => {
      await setUserOffline(user.uid);
      await signOut(auth);

      sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
      setEmail("");
      setPassword("");
      setLoginMessageType("error");
      setLoginError("Güvenliğiniz için 60 dakika işlem yapılmadığı için oturumunuz kapatıldı.");
      setUserRole(null);
      setUserData(null);
    };

    const getLastActivityAt = () => Number(
      sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || 0
    );

    const scheduleSessionTimeout = () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }

      const lastActivityAt = getLastActivityAt() || Date.now();
      const elapsedTime = Date.now() - lastActivityAt;
      const remainingTime = SESSION_TIMEOUT_MS - elapsedTime;

      if (remainingTime <= 0) {
        logoutDueToInactivity();
        return;
      }

      sessionTimeoutRef.current = setTimeout(
        logoutDueToInactivity,
        remainingTime
      );
    };

    const checkSessionTimeout = () => {
      const lastActivityAt = getLastActivityAt();

      if (lastActivityAt && Date.now() - lastActivityAt >= SESSION_TIMEOUT_MS) {
        logoutDueToInactivity();
        return;
      }

      scheduleSessionTimeout();
    };

    const resetSessionTimeout = () => {
      const lastActivityAt = getLastActivityAt();

      if (lastActivityAt && Date.now() - lastActivityAt >= SESSION_TIMEOUT_MS) {
        logoutDueToInactivity();
        return;
      }

      sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
      scheduleSessionTimeout();
    };

    const activityEvents = [
      "click",
      "keydown",
      "mousemove",
      "scroll",
      "touchstart"
    ];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetSessionTimeout, { passive: true });
    });

    window.addEventListener("focus", checkSessionTimeout);
    window.addEventListener("pageshow", checkSessionTimeout);
    document.addEventListener("visibilitychange", checkSessionTimeout);

    checkSessionTimeout();

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetSessionTimeout);
      });

      window.removeEventListener("focus", checkSessionTimeout);
      window.removeEventListener("pageshow", checkSessionTimeout);
      document.removeEventListener("visibilitychange", checkSessionTimeout);

      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
    };
  }, [user]);

  const handleLogin = async () => {
    setLoginError("");

    try {
      await setPersistence(auth, browserSessionPersistence);

      await signInWithEmailAndPassword(
        auth,
        email.trim().toLowerCase(),
        password
      );
    } catch (error) {
      console.error(error);

      setLoginError(
        "E-posta adresi veya şifre hatalı. Lütfen tekrar deneyin."
      );
    }
  };

  
  const handlePasswordReset = async () => {
    setLoginError("");

    if (!email) {
      setLoginMessageType("error");
      setLoginError("Lütfen önce e-posta adresinizi giriniz.");
      return;
    }

    try {
      await sendPasswordResetEmail(
        auth,
        email.trim().toLowerCase()
      );

      setLoginMessageType("success");
      setLoginError(
        "Şifre belirleme / sıfırlama bağlantısı e-posta adresinize gönderildi."
      );
      } catch (error) {
      setLoginMessageType("error");
      setLoginError(
        "Bu e-posta adresi için kayıtlı kullanıcı bulunamadı."
      );
    }
  };

  const handleLogout = async () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }

    if (user?.uid) {
      await setUserOffline(user.uid);
    }

    await signOut(auth);

    sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    setEmail("");
    setPassword("");
    setLoginError("");
    setUserRole(null);
    setUserData(null);
  };

  if (authLoading || (user && !userRole)) {
    return (
      <div className="aurora-loading-screen">
        <span className="aurora-loading-mark">
          <img src="/argevio-logo.svg" alt="" />
        </span>
        <strong>Çalışma alanınız hazırlanıyor</strong>
      </div>
    );
  }

  if (user) {
    return (
      <div style={{ position: "relative" }}>
        <Dashboard
          user={user}
          userRole={userRole}
          userData={userData}
          onLogout={handleLogout}
          onUserDataUpdate={(nextUserData) => {
            setUserData((current) => ({
              ...(current || {}),
              ...(nextUserData || {})
            }));
          }}
        />
        <PwaInstallPrompt />
      </div>
    );
  }

  return (
    <main className={`aurora-login${isCustomerPortal ? " is-customer" : ""}`}>
      <PwaInstallPrompt />
      <span className="aurora-login-orb aurora-login-orb-one" aria-hidden="true" />
      <span className="aurora-login-orb aurora-login-orb-two" aria-hidden="true" />

      <section className="aurora-login-intro">
        <span className="aurora-login-kicker">Ar-Ge yönetiminde yeni çalışma alanı</span>
        <h1>Süreçlerinizi daha sakin, net ve hızlı yönetin.</h1>
        <p>
          Projeler, aylık süreçler, mali raporlar ve uzman desteği tek bir akışta buluşur.
        </p>
        <div className="aurora-login-highlights" aria-label="Platform özellikleri">
          <span>Canlı süreç takibi</span>
          <span>Akıllı raporlama</span>
          <span>Teknopark AI</span>
        </div>
      </section>

      <form
        className="aurora-login-card"
        onSubmit={(event) => {
          event.preventDefault();
          handleLogin();
        }}
      >
        <div className="aurora-login-brand">
          <img
            className="aurora-login-logo"
            src="/argevio-flow-logo-light.svg"
            alt="Ar-Ge vio — Ar-Ge Operasyon Platformu"
          />
        </div>

        <div className="aurora-login-heading">
          <span>Hoş geldiniz</span>
          <h2>Çalışma alanınıza giriş yapın</h2>
        </div>

        {isSupportTestLogin && (
          <div className="aurora-login-test-note">
            Müşteri gözüyle test oturumu<br />
            İşlemler {requestedSupportEmail} olarak loglanır.
          </div>
        )}

        <label className="aurora-login-field">
          <span>E-posta adresi</span>
          <input
            type="email"
            autoComplete="email"
            placeholder="ornek@firma.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setLoginError("");
            }}
          />
        </label>

        <label className="aurora-login-field">
          <span>Şifre</span>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Şifrenizi girin"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setLoginError("");
            }}
          />
        </label>

        {loginError && (
          <div className={`aurora-login-message is-${loginMessageType}`} role="status">
            {loginError}
          </div>
        )}

        <button type="submit" className="aurora-login-submit">Giriş Yap</button>
        <button type="button" className="aurora-login-reset" onClick={handlePasswordReset}>
          Şifremi Belirle / Unuttum
        </button>

        <small className="aurora-login-security">Güvenli ve tenant ayrımlı çalışma alanı</small>
      </form>
    </main>
  );
}

export default App;
